import type { AxiosError } from "axios";
import { createContext, useContext, useState, useEffect } from "react";
import { useRequestService } from "@/hooks/useRequests";
import { useQueryClient } from "react-query";
import { cacheKey } from "@/hooks/cacheStateKey";
import useToast from "@/hooks/useToast";

export const AccessRequestContext = createContext<IAccessRequest | undefined>(
	undefined,
);

export const AccessRequestProvider = ({
	children,
}: AccessRequestProviderProps) => {
	const {
		useGetRequestHistory,
		useGetRequestComments,
		useGetRequestTasks,
		useProcessTasks,
		useCreateRequestComment,
		useEnableRequest,
		useRetryAction,
	} = useRequestService();

	const queryClient = useQueryClient();
	const { success: successToast, error: errorToast } = useToast();
	const [requestId, setRequestId] = useState<string>("");
	const [commentQueryPageIndex, setCommentQueryPageIndex] = useState(0);
	const [commentQueryPageSize, setCommentQueryPageSize] = useState(10);
	const [history, setHistory] = useState<TasksHistory>([]);
	const [comments, setComments] = useState<CommentResponse>();
	const [allTasks, setAllTasks] = useState<TasksPayload | null>(null);
	const [tasks, setTasks] = useState<Task[]>();
	const [pendingTasks, setPendingTasks] = useState<Task[]>();
	const [loadingAction, setLoadingAction] = useState(null);
	const [requestDetails, setRequestDetails] = useState<RequestDetails>();
	const [incidents, setIncidents] = useState<Incident[]>([]);
	const { mutate: processUserTask, isLoading: performingAction } =
		useProcessTasks();

	// const { mutate: retryAction, isLoading: performingRetryingAction } =
	// useRetryAction();
	const { mutate: enableRequest, isLoading: isRevokingRequest } =
		useEnableRequest();
	const { mutate: postUserComment, isLoading: postingComment } =
		useCreateRequestComment();

	const { data: taskHistory, isLoading: isLoadingHistory } =
		useGetRequestHistory(requestId);
	const { data: taskComments, isLoading: isLoadingComments } =
		useGetRequestComments(
			requestId,
			commentQueryPageIndex,
			commentQueryPageSize,
		);
	const { data: tasksData, isLoading: isLoadingTasks } =
		useGetRequestTasks(requestId);

	const { mutate: retryFailedAction, isLoading: retryingRequest  } = useRetryAction()

	useEffect(() => {
		if (taskHistory) {
			setHistory(taskHistory);
		}
	}, [taskHistory]);

	useEffect(() => {
		if (taskComments) {
			setComments(taskComments);
		}
	}, [taskComments]);

	useEffect(() => {
		if (tasksData) {
			setAllTasks(tasksData);
			setRequestDetails(tasksData.requestDetails);
			setTasks(tasksData.tasks);
			setIncidents(tasksData.incidents);
		}
	}, [tasksData]);

	const fetchTaskHistory = (requestId: string) => {
		setRequestId(requestId);
	};

	const fetchComments = ({
		requestId,
		queryPageIndex,
		queryPageSize,
	}: GetCommentInput) => {
		setRequestId(requestId);
		setCommentQueryPageIndex(queryPageIndex);
		setCommentQueryPageSize(queryPageSize);
	};

	const fetchTasks = (requestId: string) => {
		setRequestId(requestId);
	};

	const performAction = (action: string, taskId: string) => {
		setLoadingAction(action);
		processUserTask(
			{
				response: action,
				taskId,
			},
			{
				onSuccess: () => {
					queryClient.invalidateQueries(cacheKey.accessRequestTaskHistory);
					queryClient.invalidateQueries(cacheKey.accessRequestTasks);
					successToast("Task Completed");
				},
				onError: (error: AxiosError<ErrorResponse>) => {
					errorToast(
						error?.response?.data?.message ||
							error?.message ||
							"An error occured",
					);
				},
				onSettled: () => {
					setLoadingAction(null);
				},
			},
		);
		return action;
	};

	const postComment = (payload: CreateComment) => {
		postUserComment(payload, {
			onSuccess: () => {
				queryClient.invalidateQueries(cacheKey.accessRequestTaskHistory);
				queryClient.invalidateQueries(cacheKey.accessRequestComments);
			},
			onError: (error: AxiosError<ErrorResponse>) => {
				errorToast(
					error?.response?.data?.message ||
						error?.message ||
						"An error occured",
				);
			},
		});
	};

	const revokeAccessRequest = (requestId: number) => {
		enableRequest(
			{
				requestId,
				enable: false,
			},
			{
				onSuccess: () => {
					queryClient.invalidateQueries(cacheKey.accessRequestTaskHistory);
					queryClient.invalidateQueries(cacheKey.accessRequestTasks);
					successToast("Access Request Has Been Revoked");
				},
				onError: () => {
					errorToast("An error occured");
				},
			},
		);
	};

	const retryAction = (incident: Incident) => {
		retryFailedAction(
			{ incidentId: incident.id },
			{
				onSuccess: () => {
					queryClient.invalidateQueries(cacheKey.accessRequestTaskHistory);
					queryClient.invalidateQueries(cacheKey.accessRequestTasks);
					successToast("Task Completed");
				},
				onError: (error: AxiosError<ErrorResponse>) => {
					errorToast(
						error?.response?.data?.message ||
							error?.message ||
							"An error occured",
					);
				},
				onSettled: () => {
					setLoadingAction(null);
				},
			},
		);
	};

	const values: IAccessRequest = {
		fetchTaskHistory,
		fetchComments,
		fetchTasks,
		setCommentQueryPageIndex,
		setCommentQueryPageSize,
		performAction,
		postComment,
		setLoadingAction,
		retryAction,
		setPendingTasks,
		revokeAccessRequest,
		setIncidents,
		incidents,
		isRevokingRequest,
		loadingAction,
		allTasks,
		isLoadingHistory,
		history,
		comments,
		isLoadingTasks,
		tasks,
		requestDetails,
		isLoadingComments,
		commentQueryPageIndex,
		commentQueryPageSize,
		requestId,
		performingAction,
		postingComment,
		pendingTasks,
		retryingRequest,
	};

	return (
		<AccessRequestContext.Provider value={values}>
			{children}
		</AccessRequestContext.Provider>
	);
};

export const useAccessRequest = () => {
	const accessRequestContext = useContext(AccessRequestContext);
	if (accessRequestContext === undefined) {
		throw new Error(
			"useAccessRequest must be used within the Access Request Provider",
		);
	}

	return accessRequestContext;
};
